import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { AlwaysOnConditions, Channel, channelMark, channelName, REACH_CHANNELS } from './reach';

/**
 * Reach → Campaigns: set up or change an always-on campaign — "when an article
 * matching these words is published, post it to these channels at these
 * times". The conditions are the apiserver's `conditions` object
 * (reach/services/alwaysOn.js); the form is a plain view of it.
 */

const CREATE_ALWAYS_ON = gql`
  mutation CreateAlwaysOnCampaign($input: CreateProjectInput!) {
    createProject(input: $input) {
      id
    }
  }
`;
const UPDATE_ALWAYS_ON = gql`
  mutation UpdateAlwaysOnCampaign($id: ID!, $input: UpdateProjectInput!) {
    updateProject(id: $id, input: $input) {
      id
    }
  }
`;

export interface AlwaysOnCampaign {
  id: string;
  name: string;
  campaignStory?: string | null;
  conditions?: AlwaysOnConditions | null;
  channels: Array<{ id: string }>;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MEDIA = [
  ['bookmark', 'Links'],
  ['photo', 'Photos'],
  ['video', 'Videos'],
] as const;
const POST_TYPES = [
  ['auto', 'Link or text'],
  ['photo', 'Photo'],
  ['video', 'Video'],
  ['random', 'Mix of links and photos'],
] as const;
type ScheduleMode = 'asap' | 'times' | 'interval' | 'marks';

const words = (v: string) => [
  ...new Set(
    v
      .split(/[,\n]/)
      .map((w) => w.trim())
      .filter(Boolean)
  ),
];
const hm = (v: string) => (/^\d{1,2}:\d{2}$/.test(v) ? v.padStart(5, '0') : null);

interface Form {
  name: string;
  story: string;
  channelIds: Set<string>;
  include: string;
  exclude: string;
  mediaTypes: Set<string>;
  postType: string;
  maxAgeHours: string;
  delayHours: string;
  mode: ScheduleMode;
  times: string;
  intervalMinutes: string;
  marks: Set<number>;
  days: Set<number>;
  start: string;
  end: string;
  mixedHours: boolean;
  legacyFilter: boolean;
  dropLegacy: boolean;
}

function fromCampaign(c: AlwaysOnCampaign | null): Form {
  const k = c?.conditions || {};
  const schedule = k.schedule || [];
  let mode: ScheduleMode = 'asap';
  let times: string[] = [];
  let intervalMinutes = '120';
  let marks = new Set<number>();
  if (schedule.some((e) => e.time)) {
    mode = 'times';
    times = schedule.map((e) => e.time).filter(Boolean) as string[];
  } else if (schedule.length === 1 && (schedule[0].minuteMark ?? 0) === 0) {
    mode = 'interval';
    intervalMinutes = String(schedule[0].intervalMinutes || 60);
  } else if (schedule.length) {
    mode = 'marks';
    marks = new Set(schedule.map((e) => Number(e.minuteMark) || 0));
  }
  const windows = k.windows || [];
  const first = windows[0];
  const mixedHours = windows.some(
    (w) => w.startTime !== first?.startTime || w.endTime !== first?.endTime
  );
  return {
    name: c?.name || '',
    story: c?.campaignStory || '',
    channelIds: new Set((c?.channels || []).map((ch) => ch.id)),
    include: (k.include || []).join(', '),
    exclude: (k.exclude || []).join(', '),
    mediaTypes: new Set((k.mediaTypes || []).map((m) => m.toLowerCase())),
    postType: k.postType && k.postType !== 'post' ? k.postType : 'auto',
    maxAgeHours: String(k.maxAgeHours ?? 24),
    delayHours: String(k.delayHours ?? 0),
    mode,
    times: times.join(', '),
    intervalMinutes,
    marks,
    days: new Set(windows.map((w) => w.dayOfWeek)),
    start: first?.startTime?.slice(0, 5) || '09:00',
    end: first?.endTime?.slice(0, 5) || '17:00',
    mixedHours,
    legacyFilter: Boolean(k.filterSql),
    dropLegacy: false,
  };
}

type ConditionsPatch = Omit<AlwaysOnConditions, 'filterSql'> & { filterSql?: null };

function toConditions(f: Form): ConditionsPatch {
  let schedule: AlwaysOnConditions['schedule'] = [];
  if (f.mode === 'times') schedule = words(f.times).map((t) => ({ time: hm(t) || t }));
  else if (f.mode === 'interval')
    schedule = [{ minuteMark: 0, intervalMinutes: Math.max(1, Number(f.intervalMinutes) || 60) }];
  else if (f.mode === 'marks')
    schedule = [...f.marks]
      .sort((a, b) => a - b)
      .map((m) => ({ minuteMark: m, intervalMinutes: 60 }));
  const start = hm(f.start) || '00:00';
  const end = hm(f.end) || '23:59';
  const windows = [...f.days]
    .sort()
    .map((d) => ({ dayOfWeek: d, startTime: start, endTime: end, spansMidnight: end < start }));
  return {
    postType: f.postType,
    maxAgeHours: Math.max(1, Number(f.maxAgeHours) || 24),
    delayHours: Math.max(0, Number(f.delayHours) || 0),
    schedule,
    windows,
    include: words(f.include),
    exclude: words(f.exclude),
    mediaTypes: [...f.mediaTypes],
    ...(f.dropLegacy ? { filterSql: null } : {}),
  };
}

export function AlwaysOnEditor({
  campaign,
  onClose,
  onSaved,
}: {
  /** Null to set up a new one. */
  campaign: AlwaysOnCampaign | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { accountId } = useAccount();
  const { data } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
  });
  const channels = useMemo(() => (data?.socialProfiles || []).filter((c) => c.isActive), [data]);
  const [f, setF] = useState<Form>(() => fromCampaign(campaign));
  useEffect(() => setF(fromCampaign(campaign)), [campaign]);
  const [create] = useMutation<{ createProject: { id: string } }>(CREATE_ALWAYS_ON);
  const [update] = useMutation<{ updateProject: { id: string } }>(UPDATE_ALWAYS_ON);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setF((p) => ({ ...p, [key]: value }));
  const toggle = <T,>(key: 'channelIds' | 'mediaTypes' | 'marks' | 'days', value: T) =>
    setF((p) => {
      const next = new Set(p[key] as Set<T>);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...p, [key]: next };
    });

  const timesOk = f.mode !== 'times' || words(f.times).every((t) => hm(t));
  const canSave = f.name.trim().length > 0 && f.channelIds.size > 0 && timesOk && !busy;

  const save = async () => {
    if (!accountId || !canSave) return;
    setBusy(true);
    try {
      const conditions = toConditions(f);
      const common = {
        name: f.name.trim(),
        campaignStory: f.story.trim() || null,
        conditions,
        channelIds: [...f.channelIds],
      };
      let id: string;
      if (campaign) {
        const { data: res } = await update({ variables: { id: campaign.id, input: common } });
        id = res?.updateProject.id || campaign.id;
        showInkToast(`${common.name}: saved.`);
      } else {
        const { data: res } = await create({
          variables: { input: { accountId, kind: 'always_on', status: 'active', ...common } },
        });
        id = res?.createProject.id || '';
        showInkToast(`${common.name} is on.`);
      }
      onSaved(id);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  const approvalNote = useMemo(() => {
    const picked = channels.filter((c) => f.channelIds.has(c.id));
    const auto = picked
      .filter((c) => c.approvalMode === 'auto')
      .map((c) => channelName(c.platform));
    const ask = picked.filter((c) => c.approvalMode !== 'auto').map((c) => channelName(c.platform));
    return [
      auto.length ? `${auto.join(', ')} ${auto.length === 1 ? 'posts' : 'post'} on its own.` : '',
      ask.length ? `${ask.join(', ')} ${ask.length === 1 ? 'asks' : 'ask'} you first.` : '',
    ]
      .filter(Boolean)
      .join(' ');
  }, [channels, f.channelIds]);

  return (
    <div
      className="ink-modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="ink-modal aoe"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aoe-title"
        style={{ width: 'min(640px, 100%)' }}
      >
        <div className="ink-modal-title" id="aoe-title">
          {campaign ? 'Change always-on campaign' : 'New always-on campaign'}
        </div>
        <p className="ink-modal-copy">
          When an article matching these conditions is published, your agent posts it to the
          channels you pick, at the next allowed time.
        </p>

        <div className="aoe-form">
          <label className="ink-site-label">
            Name
            <input
              className="ink-site-input"
              value={f.name}
              autoFocus
              maxLength={200}
              placeholder="e.g. Share new articles"
              onChange={(e) => set('name', e.target.value)}
            />
          </label>
          <label className="ink-site-label">
            What it does <span className="ink-muted">(optional)</span>
            <input
              className="ink-site-input"
              value={f.story}
              maxLength={300}
              placeholder="e.g. Every MLB and NCAAF article, one post per channel"
              onChange={(e) => set('story', e.target.value)}
            />
          </label>

          <div className="ink-site-label">
            Channels
            {channels.length ? (
              <div className="chp-list">
                {channels.map((c) => (
                  <label className="chp-row" key={c.id}>
                    <input
                      type="checkbox"
                      checked={f.channelIds.has(c.id)}
                      onChange={() => toggle('channelIds', c.id)}
                    />
                    <span className="rc-mark rc-mark-sm">{channelMark(c.platform)}</span>
                    <span className="chp-name">
                      <span>{c.profileName || c.profileUsername || channelName(c.platform)}</span>
                      <span className="ink-muted">
                        {channelName(c.platform)} ·{' '}
                        {c.approvalMode === 'auto' ? 'Posts on its own' : 'Asks you first'}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <span className="ink-muted">No channels connected yet. Connect one in Channels.</span>
            )}
            {approvalNote ? (
              <span className="ink-muted aoe-help">
                {approvalNote} Change that per channel in Reach → Channels.
              </span>
            ) : null}
          </div>

          <div className="aoe-two">
            <label className="ink-site-label">
              Articles that mention <span className="ink-muted">(any of)</span>
              <input
                className="ink-site-input"
                value={f.include}
                placeholder="e.g. MLB, NCAAF — blank for every article"
                onChange={(e) => set('include', e.target.value)}
              />
            </label>
            <label className="ink-site-label">
              But not
              <input
                className="ink-site-input"
                value={f.exclude}
                placeholder="e.g. injury"
                onChange={(e) => set('exclude', e.target.value)}
              />
            </label>
          </div>

          <div className="ink-site-label">
            Kinds of article
            <div className="aoe-pills">
              {MEDIA.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className="aoe-pill"
                  data-active={f.mediaTypes.has(key) ? 'true' : undefined}
                  onClick={() => toggle('mediaTypes', key)}
                >
                  {label}
                </button>
              ))}
              <span className="ink-muted">None picked = every kind.</span>
            </div>
          </div>

          <div className="ink-site-label">
            Post as
            <span className="ink-seg" style={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}>
              {POST_TYPES.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  data-active={f.postType === key}
                  onClick={() => set('postType', key)}
                >
                  {label}
                </button>
              ))}
            </span>
          </div>

          <div className="aoe-two">
            <label className="ink-site-label">
              Only articles newer than
              <span className="aoe-unit">
                <input
                  className="ink-site-input"
                  type="number"
                  min={1}
                  value={f.maxAgeHours}
                  onChange={(e) => set('maxAgeHours', e.target.value)}
                />
                hours
              </span>
            </label>
            <label className="ink-site-label">
              Wait after publishing
              <span className="aoe-unit">
                <input
                  className="ink-site-input"
                  type="number"
                  min={0}
                  value={f.delayHours}
                  onChange={(e) => set('delayHours', e.target.value)}
                />
                hours
              </span>
            </label>
          </div>

          <div className="ink-site-label">
            When to post
            <span className="ink-seg" style={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}>
              {(
                [
                  ['asap', 'As soon as allowed'],
                  ['times', 'At set times'],
                  ['interval', 'Every so often'],
                  ['marks', 'Past each hour'],
                ] as Array<[ScheduleMode, string]>
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  data-active={f.mode === key}
                  onClick={() => set('mode', key)}
                >
                  {label}
                </button>
              ))}
            </span>
            {f.mode === 'times' ? (
              <input
                className="ink-site-input"
                value={f.times}
                placeholder="e.g. 09:00, 12:30, 17:00"
                onChange={(e) => set('times', e.target.value)}
                aria-invalid={!timesOk}
              />
            ) : null}
            {f.mode === 'interval' ? (
              <span className="aoe-unit">
                every
                <input
                  className="ink-site-input"
                  type="number"
                  min={5}
                  step={5}
                  value={f.intervalMinutes}
                  onChange={(e) => set('intervalMinutes', e.target.value)}
                />
                minutes
              </span>
            ) : null}
            {f.mode === 'marks' ? (
              <div className="aoe-pills">
                {[0, 15, 30, 45].map((m) => (
                  <button
                    key={m}
                    type="button"
                    className="aoe-pill"
                    data-active={f.marks.has(m) ? 'true' : undefined}
                    onClick={() => toggle('marks', m)}
                  >
                    :{String(m).padStart(2, '0')}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="ink-site-label">
            Only on <span className="ink-muted">(none picked = any day, any time)</span>
            <div className="aoe-pills">
              {DAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  className="aoe-pill"
                  data-active={f.days.has(i) ? 'true' : undefined}
                  onClick={() => toggle('days', i)}
                >
                  {d}
                </button>
              ))}
            </div>
            {f.days.size ? (
              <span className="aoe-unit">
                between
                <input
                  className="ink-site-input"
                  type="time"
                  value={f.start}
                  onChange={(e) => set('start', e.target.value)}
                />
                and
                <input
                  className="ink-site-input"
                  type="time"
                  value={f.end}
                  onChange={(e) => set('end', e.target.value)}
                />
                {f.mixedHours ? (
                  <span className="ink-muted">
                    Hours differed by day; saving sets these hours for every day picked.
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>

          {f.legacyFilter ? (
            <div className="aoe-legacy">
              <span>
                <strong>Custom filter (legacy)</strong> — a filter from the old rule still applies
                on top of the words above. It can’t be edited here.
              </span>
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                data-active={f.dropLegacy ? 'true' : undefined}
                onClick={() => set('dropLegacy', !f.dropLegacy)}
              >
                {f.dropLegacy ? 'Will be removed on save' : 'Remove it'}
              </button>
            </div>
          ) : null}
        </div>

        <div className="ink-modal-actions">
          <button type="button" className="ink-btn ink-btn-quiet" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={!canSave}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : campaign ? 'Save' : 'Turn it on'}
          </button>
        </div>
      </div>
    </div>
  );
}
